import React, { useState, useEffect } from "react";
import {
  Authenticator,
  ThemeProvider,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import "@aws-amplify/ui-react/styles.css";
import { theme } from "../theme";
import Sidebar from "../components/Sidebar";
import ChatInterface from "./ChatInterface";
import axios from "axios";
import { getUserId } from "../utils/userUtils";
import UsageDisplay from "../components/usage/UsageDisplay";
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Amplify is already configured in index.js with the correct configuration

function ControlPanelContent({ user, signOut }) {
  const { t } = useTranslation();
  const [selectedChatBotId, setSelectedChatBotId] = useState(null);
  const [subscribedProducts, setSubscribedProducts] = useState([]);

  // Function to save the selected bot to localStorage
  const saveSelectedBot = (botId) => {
    localStorage.setItem("lastSelectedBot", botId);
  };

  // Function to get the last selected bot from localStorage
  const getLastSelectedBot = () => {
    return localStorage.getItem("lastSelectedBot");
  };

  useEffect(() => {
    if (!user) return; // Ensure the user is available

    const fetchSubscriptions = async () => {
      try {
        const userId = getUserId(user);

        if (!userId) {
          console.error("No user ID available");
          setSubscribedProducts([]);
          return;
        }

        const response = await axios.get(
          `https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions/${userId}`
        );

        const subscription = response.data;

        // Initialize activeSubscription outside the if-else blocks
        let activeSubscription = [];

        // Check if the subscription is active
        if (subscription.subscription_status === "active") {
          // Access the allowed_bots array and filter out Jarvis (bot ID 0)
          const allowedBots = subscription.allowed_bots || [];
          console.log("Allowed Bots:", allowedBots);
          activeSubscription = allowedBots.filter(botId => botId !== "0" && botId !== 0);
          console.log("Filtered Bots (excluding Jarvis):", activeSubscription);
        } else {
          console.log("No active subscription found.");
          activeSubscription = [];
        }

        setSubscribedProducts(activeSubscription);

        // Get the last selected bot from localStorage
        const lastSelectedBot = getLastSelectedBot();

        // Check if the last selected bot is in the active subscriptions
        if (lastSelectedBot && activeSubscription.includes(lastSelectedBot)) {
          setSelectedChatBotId(lastSelectedBot);
        } else if (activeSubscription.length > 0) {
          // Fall back to selecting the first available bot
          setSelectedChatBotId(activeSubscription[0]);
          saveSelectedBot(activeSubscription[0]);
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        setSubscribedProducts([]);
      }
    };

    fetchSubscriptions();
  }, [user]);

  // Wrap the original setSelectedChatBotId to also save to localStorage
  const handleBotSelection = (botId) => {
    setSelectedChatBotId(botId);
    saveSelectedBot(botId);
  };

  return (
    <div className="bg-gray-900 text-white">
      <Sidebar
        user={user}
        setSelectedChatBotId={handleBotSelection}
        subscribedProducts={subscribedProducts}
      />
      <div className="grid-cols-12">
        <div className="sm:col-span-12 bg-gray-800 shadow-lg p-2">
          {selectedChatBotId ? (
            <ChatInterface chatBotId={selectedChatBotId} />
          ) : (
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Media Usage</h3>
                  <UsageDisplay 
                    mediaType="picture" 
                    showTitle={false}
                    showUpgradeButton={true}
                    compact={false}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">{t('controlPanel.gettingStarted')}</h3>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <p className="text-gray-300 mb-4">
                      {t('controlPanel.selectCharacter')}
                    </p>
                    <div className="mt-4">
                      <Link 
                        to="/subscription-management" 
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
                      >
                        {t('controlPanel.manageSubscription')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ControlPanel() {
  return (
    <ThemeProvider theme={theme}>
      <Authenticator
        loginMechanisms={["email"]}
        socialProviders={["google"]}
        initialState="signIn"
        services={{
          handleSignIn: async (formData) => {
            const { username, password } = formData;
            return Amplify.Auth.signIn({
              username,
              password,
            });
          },
        }}
      >
        {({ signOut, user }) =>
          user ? (
            <ControlPanelContent user={user} signOut={signOut} />
          ) : (
            <div>Loading...</div>
          )
        }
      </Authenticator>
    </ThemeProvider>
  );
}
